import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Target, ChevronLeft, ChevronRight, Copy } from 'lucide-react';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
export const CAMPANHAS = [['energia', 'Energias'], ['telecom', 'Telecom'], ['alarmes', 'Alarmes']];
const MESES = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const pad = (n) => String(n).padStart(2, '0');
const fmtD = (iso) => { const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
export const periodoLabel = (r) => (r.tipo === 'mes' ? `${MESES[Number(r.periodo.slice(5, 7)) - 1]} ${r.periodo.slice(0, 4)}` : `Semana ${fmtD(r.inicio)} a ${fmtD(r.fim)}`);

const shift = (tipo, periodo, dir) => {
  if (tipo === 'mes') {
    let [y, m] = periodo.split('-').map(Number);
    m += dir; if (m < 1) { m = 12; y -= 1; } if (m > 12) { m = 1; y += 1; }
    return `${y}-${pad(m)}`;
  }
  const [y, m, d] = periodo.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 7 * dir));
  return `${dt.getUTCFullYear()}-${pad(dt.getUTCMonth() + 1)}-${pad(dt.getUTCDate())}`;
};

const Cell = ({ row, k, editable, onSave }) => {
  const meta = row.metas[k];
  const feito = row.feitos[k];
  const [v, setV] = useState(meta || '');
  useEffect(() => setV(meta || ''), [meta]);
  const pct = meta ? Math.min(100, Math.round((100 * feito) / meta)) : 0;
  return (
    <td className="px-3 py-2 align-top" data-testid={`meta-cell-${row.id}-${k}`}>
      <div className="flex items-center gap-1.5">
        {editable ? (
          <input type="number" min="0" max="1000" value={v} onChange={(e) => setV(e.target.value)}
            onBlur={() => { if (Number(v || 0) !== Number(meta || 0)) onSave(row, k, v === '' ? 0 : Number(v)); }}
            className="w-14 border border-slate-300 rounded-md px-1.5 py-1 text-sm text-center" data-testid={`meta-input-${row.id}-${k}`} />
        ) : <span className="text-sm font-semibold w-8">{meta || '—'}</span>}
        <span className="text-xs text-slate-600 whitespace-nowrap"><b className="text-slate-800">{feito}</b>{meta ? ` / ${meta}` : ''}</span>
      </div>
      {meta > 0 && <div className="h-1.5 rounded-full bg-slate-100 mt-1.5 w-24"><div className={`h-full rounded-full ${pct >= 100 ? 'bg-emerald-500' : 'bg-[#4DD4E8]'}`} style={{ width: `${pct}%` }} /></div>}
    </td>
  );
};

export default function MetasPage() {
  const { user } = useAuth();
  const allowed = ['super_admin', 'gestor', 'chefe_equipa'].includes(user?.role);
  const [tipo, setTipo] = useState(new URLSearchParams(window.location.search).get('tipo') === 'semana' ? 'semana' : 'mes');
  const [periodo, setPeriodo] = useState(null);
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    axios.get(`${API_URL}/metas/equipa`, { params: { tipo, ...(periodo ? { periodo } : {}) } })
      .then((r) => { setD(r.data); if (!periodo) setPeriodo(r.data.periodo); }).catch(() => setD(null));
  }, [tipo, periodo]);
  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const save = async (row, k, val) => {
    const body = { user_id: row.id, tipo, periodo: d.periodo };
    CAMPANHAS.forEach(([c]) => { body[c] = c === k ? val : (row.metas[c] || 0); });
    try { await axios.put(`${API_URL}/metas`, body); toast.success(`Meta de ${row.name} guardada`); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao guardar'); }
  };

  const copiar = async (substituir = false) => {
    try {
      const { data } = await axios.post(`${API_URL}/metas/copiar`, { tipo, periodo: d.periodo, substituir });
      if (!data.ok) {
        if (window.confirm(`O período seguinte já tem ${data.existentes} meta(s). Substituir?`)) copiar(true);
        return;
      }
      toast.success(`${data.copiadas} meta(s) copiada(s)`);
      setPeriodo(shift(tipo, d.periodo, 1));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Erro ao copiar'); }
  };
  const reset = async (row) => {
    try { await axios.put(`${API_URL}/metas`, { user_id: row.id, tipo, periodo: d.periodo }); toast.success('Meta do mês reposta'); load(); } catch { toast.error('Erro'); }
  };

  if (!allowed) return <Layout><p className="p-6 text-sm text-slate-500">Sem permissão para ver esta página.</p></Layout>;
  const tot = (k) => (d?.comerciais || []).reduce((a, r) => [a[0] + r.feitos[k], a[1] + r.metas[k]], [0, 0]);
  return (
    <Layout>
      <div className="space-y-5" data-testid="metas-page">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-[Outfit] text-[#2D3E6F] flex items-center gap-2"><Target className="w-7 h-7" />Metas dos comerciais</h1>
          <p className="text-sm text-slate-500 mt-1">Defina metas por mês ou por semana, separadas por campanha. Pode preparar já os próximos meses: só contam os contratos criados dentro do período.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {[['mes', 'Mês'], ['semana', 'Semana']].map(([k, l]) => (
            <button key={k} type="button" onClick={() => { setTipo(k); setPeriodo(null); }} data-testid={`metas-tipo-${k}`}
              className={`text-sm px-4 py-1.5 rounded-full border transition-colors ${tipo === k ? 'bg-[#2D3E6F] text-white border-[#2D3E6F]' : 'bg-white border-slate-200 text-slate-600'}`}>{l}</button>
          ))}
          {d && (
            <div className="flex items-center gap-1 ml-2">
              <button type="button" onClick={() => setPeriodo(shift(tipo, d.periodo, -1))} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="metas-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-semibold text-slate-800 min-w-[190px] text-center" data-testid="metas-periodo">{periodoLabel(d)}</span>
              <button type="button" onClick={() => setPeriodo(shift(tipo, d.periodo, 1))} className="p-1.5 rounded-full hover:bg-slate-100" data-testid="metas-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
          {d?.pode_editar && (
            <button type="button" onClick={() => copiar(false)} className="ml-auto text-sm border border-[#2D3E6F] text-[#2D3E6F] rounded-full px-4 py-1.5 flex items-center gap-1.5 hover:bg-[#2D3E6F] hover:text-white transition-colors" data-testid="metas-copiar">
              <Copy className="w-3.5 h-3.5" />Copiar para {tipo === 'mes' ? 'o mês' : 'a semana'} seguinte
            </button>
          )}
        </div>
        {tipo === 'mes' && <p className="text-xs text-slate-500">Se só definir metas semanais, a meta do mês é a soma das semanas que começam (segunda-feira) nesse mês. Pode alterá-la aqui.</p>}
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-slate-500">
              <th className="px-3 py-2">Comercial</th>
              {CAMPANHAS.map(([k, l]) => <th key={k} className="px-3 py-2">{l}</th>)}
              <th className="px-3 py-2">Total</th>
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {!d && <tr><td colSpan={5} className="p-6 text-slate-400">A carregar…</td></tr>}
              {d?.comerciais.map((r) => (
                <tr key={r.id} data-testid={`metas-row-${r.id}`}>
                  <td className="px-3 py-2 align-top">
                    <p className="font-medium text-slate-800">{r.name}</p>
                    {r.metas.auto && <span className="text-[10px] text-sky-700 bg-sky-50 rounded-full px-1.5 py-0.5">soma das semanas</span>}
                    {tipo === 'mes' && d.pode_editar && r.metas.definida && !r.metas.auto && CAMPANHAS.some(([c]) => r.metas[c] > 0) && (
                      <button type="button" onClick={() => reset(r)} className="block text-[10px] text-slate-500 hover:text-[#2D3E6F] underline mt-0.5" data-testid={`metas-reset-${r.id}`}>repor soma das semanas</button>
                    )}
                    {r.metas.total > 0 && <span className="text-[10px] text-slate-600 bg-slate-100 rounded-full px-1.5 py-0.5 ml-1">meta antiga total: {r.metas.total}</span>}
                  </td>
                  {CAMPANHAS.map(([k]) => <Cell key={k} row={r} k={k} editable={d.pode_editar} onSave={save} />)}
                  <td className="px-3 py-2 align-top font-semibold whitespace-nowrap" data-testid={`metas-total-${r.id}`}>{r.feitos.total} / {r.meta_total || '—'}{r.pct != null ? ` · ${r.pct}%` : ''}</td>
                </tr>
              ))}
              {d?.comerciais?.length > 0 && (
                <tr className="bg-slate-50 font-semibold" data-testid="metas-row-total">
                  <td className="px-3 py-2">Total equipa</td>
                  {CAMPANHAS.map(([k]) => { const [f, m] = tot(k); return <td key={k} className="px-3 py-2">{f} / {m || '—'}</td>; })}
                  <td className="px-3 py-2">{d.comerciais.reduce((a, r) => a + r.feitos.total, 0)} / {d.comerciais.reduce((a, r) => a + r.meta_total, 0) || '—'}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
